import { useEffect, useRef, useState } from "react";
import type { EngineInfo } from "../lib/api";

/**
 * Switch between Claude and Grok, and pick a model.
 *
 * The *engine* locks once a conversation has turns: the two CLIs keep their
 * own transcripts and cannot resume each other's, so a mid-thread swap would
 * silently drop context. The *model* stays pickable; it applies from the
 * next turn. While a turn is streaming, the whole control waits.
 */
export function EnginePicker({
  engines,
  engineId,
  model,
  engineLocked,
  modelLocked,
  buildMode,
  onChange,
  onBuildMode,
}: {
  engines: EngineInfo[];
  engineId: string;
  model: string;
  engineLocked: boolean;
  modelLocked: boolean;
  buildMode: boolean;
  onChange: (engineId: string, model: string) => void;
  onBuildMode: (on: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (modelLocked) setOpen(false);
  }, [modelLocked]);

  const current = engines.find((e) => e.id === engineId);
  const grok = engines.find((e) => e.id === "grok");
  const shown = model || current?.defaultModel || "";
  const buildBlocked =
    !grok?.available
      ? grok?.detail || "Grok is not available"
      : engineLocked && engineId !== "grok"
        ? "Build mode is Grok-only — this thread is on Claude"
        : "";
  const title = modelLocked
    ? "Wait for this turn to finish"
    : engineLocked
      ? "Model applies from the next turn"
      : "Switch engine or model";

  return (
    <div className="picker" ref={box}>
      <button
        type="button"
        className="picker__btn"
        onClick={() => !modelLocked && setOpen((v) => !v)}
        disabled={modelLocked}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={title}
      >
        <span className={`dot ${current?.available ? "dot--ok" : "dot--fault"}`} />
        <span className="picker__name">{current?.label ?? "Engine"}</span>
        <span className="picker__model mono">{shown}</span>
      </button>

      {open && (
        <div className="picker__menu" role="listbox">
          {engines.map((e) => {
            const otherEngine = engineLocked && e.id !== engineId;
            return (
              <div key={e.id} className="picker__group">
                <div className="picker__groupHead">
                  <span className={`dot ${e.available ? "dot--ok" : "dot--fault"}`} />
                  <span>{e.label}</span>
                  {!e.available && <span className="picker__why truncate">{e.detail}</span>}
                  {otherEngine && e.available && (
                    <span className="picker__why">
                      Switching engines would drop this thread's context
                    </span>
                  )}
                </div>
                {e.available &&
                  e.models.map((m) => {
                    const on = e.id === engineId && (model || current?.defaultModel) === m;
                    return (
                      <button
                        key={`${e.id}:${m}`}
                        type="button"
                        role="option"
                        aria-selected={on}
                        className={`picker__item ${on ? "is-on" : ""}`}
                        disabled={otherEngine}
                        onClick={() => {
                          if (otherEngine) return;
                          onChange(e.id, m);
                          setOpen(false);
                        }}
                      >
                        <span className="mono">{m}</span>
                        {on && <span className="picker__check">✓</span>}
                      </button>
                    );
                  })}
              </div>
            );
          })}
          <div className="picker__build">
            <label className={`picker__switch ${buildBlocked ? "is-off" : ""}`}>
              <input
                type="checkbox"
                checked={buildMode}
                disabled={Boolean(buildBlocked) || modelLocked}
                onChange={(e) => {
                  const on = e.target.checked;
                  if (on && grok?.available) onChange("grok", grok.defaultModel || model);
                  onBuildMode(on);
                }}
              />
              <span>Build with Grok</span>
            </label>
            <p className="picker__hint">
              Runs this chat as a build agent: it can edit files and run commands.
            </p>
            {buildBlocked ? <p className="picker__why">{buildBlocked}</p> : null}
          </div>
        </div>
      )}
    </div>
  );
}
